import type {Ref} from 'vue';
import {ref, toValue, watch} from 'vue';
import {apiClient} from '@/api/apiClient';

/**
 * A reusable Vue composable for data fetching.
 * @param endpoint - The API endpoint to fetch from (e.g., '/users'). Can be a ref.
 * @param options - Optional request options (e.g., method, body).
 */
export function useFetch<T>(endpoint: Ref<string | null> | string | null, options?: RequestInit) {

	const data = ref<T | null>(null);
	const error = ref<Error | null>(null);
	const isLoading = ref(false);

	const execute = async () => {
		const currentEndpoint = toValue(endpoint);

		// Do not fetch if the endpoint is null, undefined, or empty.
		// This allows for conditional fetching.
		if (!currentEndpoint) {
			data.value = null;
			return;
		}

		isLoading.value = true;
		error.value = null;
		data.value = null; // Clear previous data on new fetch

		try {
			const fetchOptions: RequestInit = {...options, credentials: "include"};
			// Delegate the actual fetch call to the centralized apiClient.
			data.value = await apiClient.fetch<T>(currentEndpoint, fetchOptions);
		} catch (e: any) {
			error.value = e;
			console.error("useFetch failed:", e);
		} finally {
			isLoading.value = false;
		}
	};

	// Watch for changes to the endpoint ref and re-fetch automatically.
	watch(
		() => toValue(endpoint),
		execute,
		{immediate: true} // Run the fetch immediately on component setup.
	);

	return {data, error, isLoading, execute};
}
